import { ConnectError } from "@connectrpc/connect";
import type { MuxPlayerRefAttributes } from "@mux/mux-player-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect } from "react";

import { SegmentContext } from "@/contexts/SegmentContext";
import { useDesktop } from "@/hooks/use-desktop";
import { getEnabledExperimentsMap } from "@/hooks/use-experiment";
import { getAccountQueryKey, useGetAccount } from "@/queries/account-queries";
import { organizationQueryKey } from "@/queries/organization-queries";
import { keys as userQueryKeys, type PlainUser } from "@/queries/user-queries";
import { extractDiagnosticMessage } from "@/utils/errors";
import { BuildVersion } from "@/utils/version";
import type { Account } from "gitpod-next-api/gitpod/v1/account_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import type { Organization } from "gitpod-next-api/gitpod/v1/organization_pb";

const useSegment = () => useContext(SegmentContext);

const getOrganizationTierString = (tier?: OrganizationTier): string => {
    switch (tier) {
        case OrganizationTier.FREE:
            return "free";
        case OrganizationTier.CORE:
            return "core";
        case OrganizationTier.ENTERPRISE:
            return "enterprise";
        case OrganizationTier.FREE_ONA:
            return "free_ona";
        default:
            return "unknown";
    }
};

export const useSegmentIdentify = () => {
    const segment = useSegment();
    const { data: account } = useGetAccount();

    useEffect(() => {
        if (!segment?.analytics) {
            return;
        }

        if (!account) {
            console.debug("Account not available");
            return;
        }

        // Storage configuration (Cookie + Memory only):
        // - anonymousId/userId stored in cookies (survives OAuth redirects)
        // - Traits (email, name, avatar) stored in memory only (no localStorage)
        segment.analytics
            .identify(account.id, {
                name: account.name,
                email: account.email,
                avatar: account.avatarUrl,
            })
            .catch(console.error);
    }, [account, segment]);
};

export const useResetSegment = () => {
    const segment = useSegment();

    return () => {
        if (!segment?.analytics) {
            return;
        }

        segment.analytics.reset().catch(console.error);
    };
};

function isMuxPlayerElement(element: Element): element is MuxPlayerRefAttributes {
    return element.tagName.toLowerCase() === "mux-player";
}

export const useSegmentTrack = () => {
    const segment = useSegment();
    const desktop = useDesktop();
    const queryClient = useQueryClient();

    return useCallback(
        (eventName: string, properties?: Readonly<Record<string, unknown>>) => {
            if (!segment?.analytics) {
                return;
            }

            // Get fresh data from cache at tracking time to avoid stale org ID after switching
            const user = queryClient.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            const account = queryClient.getQueryData<Account>(getAccountQueryKey());
            const organization = queryClient.getQueryData<Organization>(organizationQueryKey.get(user?.organizationId));

            const props = { ...properties };
            if (user) {
                // see https://www.notion.so/gitpod/Gitpod-Next-Data-Tracking-1446425f2d528006a67de82b31b5baaf?pvs=4#1446425f2d52808dabdfd1e0e30e0630
                props.organization_user_id = user.id;
                props.auth_principal = "user";
                props.auth_id = user.id;
                props.account_id = account?.id;
                props.organization_id = user.organizationId;
                props.organization_tier = getOrganizationTierString(organization?.tier);
            }
            if (window.isWebview) {
                props.client = "vscode";
            } else if (desktop.isDesktop) {
                props.client = "desktop";
                props.desktopQuality = desktop.appMainInfo?.quality;
                props.desktopVersion = desktop.appMainInfo?.version;
            } else {
                props.client = "web";
            }
            props.dashboardVersion = BuildVersion;
            props.experiments = getEnabledExperimentsMap(user?.organizationId);
            Object.assign(props, segment.platformDetails);

            segment.analytics.track(eventName, props).catch(console.error);
        },
        [segment, desktop, queryClient],
    );
};

export const useSegmentPage = () => {
    const segment = useSegment();
    const desktop = useDesktop();
    const queryClient = useQueryClient();

    return useCallback(
        (name?: string, category?: string, properties?: Readonly<Record<string, unknown>>, referrer?: string) => {
            if (!segment?.analytics) {
                return;
            }

            // Get fresh data from cache at tracking time to avoid stale org ID after switching
            const user = queryClient.getQueryData<PlainUser>(userQueryKeys.getAuthenticatedUserQueryKey());
            const account = queryClient.getQueryData<Account>(getAccountQueryKey());
            const organization = queryClient.getQueryData<Organization>(organizationQueryKey.get(user?.organizationId));

            const props = { ...properties };
            if (user) {
                // see https://www.notion.so/gitpod/Gitpod-Next-Data-Tracking-1446425f2d528006a67de82b31b5baaf?pvs=4#1446425f2d52808dabdfd1e0e30e0630
                props.organization_user_id = user.id;
                props.auth_principal = "user";
                props.auth_id = user.id;
                props.account_id = account?.id;
                props.organization_id = user.organizationId;
                props.organization_tier = getOrganizationTierString(organization?.tier);
            }
            if (window.isWebview) {
                props.client = "vscode";
            } else if (desktop.isDesktop) {
                props.client = "desktop";
                props.desktopQuality = desktop.appMainInfo?.quality;
                props.desktopVersion = desktop.appMainInfo?.version;
            } else {
                props.client = "web";
            }
            props.dashboardVersion = BuildVersion;
            props.experiments = getEnabledExperimentsMap(user?.organizationId);
            Object.assign(props, segment.platformDetails);

            // Add referrer if provided (for SPA navigation tracking)
            if (referrer) {
                props.referrer = referrer;
            }

            // Call analytics.page() with category, name, and properties
            if (category && name) {
                segment.analytics.page(category, name, props).catch(console.error);
            } else if (name) {
                segment.analytics.page(name, props).catch(console.error);
            } else {
                segment.analytics.page(props).catch(console.error);
            }
        },
        [segment, desktop, queryClient],
    );
};

export const useSegmentTrackError = () => {
    const track = useSegmentTrack();
    return useCallback(
        (eventName: string, error: unknown) => {
            if (error instanceof ConnectError) {
                track(eventName, {
                    errorMessage: extractDiagnosticMessage(error),
                    errorCode: error.code.toString(),
                    stack: error.stack,
                });
            } else if (error instanceof Error) {
                track(eventName, {
                    errorMessage: error.message,
                    errorName: error.name,
                    stack: error.stack,
                });
            } else {
                track(eventName, {
                    errorName: "unknown",
                });
            }
        },
        [track],
    );
};

export const useSegmentTrackVideoClick = () => {
    const track = useSegmentTrack();
    return useCallback(
        (context: string) => {
            track("video_clicked", {
                context,
                path: window.location.pathname,
            });
        },
        [track],
    );
};

const findLocation = (target: HTMLElement) => {
    const element = target.closest(`[data-track-location]`);
    return element?.getAttribute("data-track-location") || undefined;
};

export const useButtonOrAnchorTracking = () => {
    const track = useSegmentTrack();

    useEffect(() => {
        const trackButtonOrAnchor = (
            target: HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | MuxPlayerRefAttributes,
        ) => {
            const trackingMsg: ButtonOrAnchorTrackingProps = {
                path: window.location.pathname,
                location: findLocation(target),
                label: anonymizeUrls(target.ariaLabel || target.textContent || undefined),
                targetTrackingId: target.getAttribute("data-tracking-id") || undefined,
            };

            if (target instanceof HTMLButtonElement || target instanceof HTMLDivElement) {
                //retrieve href if parent is an anchor element
                if (target.parentElement instanceof HTMLAnchorElement) {
                    const anchor = target.parentElement;
                    trackingMsg.destination = anchor.href;
                }
            }

            if (target instanceof HTMLAnchorElement) {
                const anchor = target;
                trackingMsg.destination = anchor.href;
            }

            track("dashboard_clicked", trackingMsg);
        };

        const handleButtonOrAnchorTracking = (props: MouseEvent) => {
            const root = document.getElementById("root");
            let curr = props.target as HTMLElement;

            while (!(curr instanceof Document) && curr !== root) {
                if (
                    curr instanceof HTMLButtonElement ||
                    curr instanceof HTMLAnchorElement ||
                    (curr instanceof HTMLDivElement && curr.onclick) ||
                    isMuxPlayerElement(curr)
                ) {
                    if (curr.getAttribute("data-track-disable") !== "true") {
                        trackButtonOrAnchor(curr);
                    }
                    break; //finding first ancestor is sufficient
                }
                curr = curr.parentNode as HTMLElement;
            }
        };

        window.addEventListener("click", handleButtonOrAnchorTracking, true);
        return () => window.removeEventListener("click", handleButtonOrAnchorTracking, true);
    });
};

type ButtonOrAnchorTrackingProps = {
    path: string;
    location?: string;
    label: string | undefined;
    destination?: string;
    // The primary tracking identifier for a clickable element
    targetTrackingId: string | undefined;
};

export const TrackLocations = {
    Command: "command",
    Sidebar: "sidebar",
    SidebarOrgSwitcher: "sidebar_org_switcher",
    SidebarEnvironmentList: "sidebar_environment_list",
    SidebarAgentExecutionList: "sidebar_agent_execution_list",
    SidebarCreateEnvironmentModal: "sidebar_create_environment_modal",
    NewPersonalAccessTokenModal: "new_personal_access_token_modal",
    DeletePersonalAccessTokenModal: "delete_personal_access_token_modal",
    DeleteOrganizationModal: "delete_organization_modal",
    InventoryStopEnvironmentModal: "inventory_stop_environment_modal",
    InventoryDeleteEnvironmentModal: "inventory_delete_environment_modal",
    InventoryForceDeleteEnvironmentModal: "inventory_force_delete_environment_modal",
    InventoryUpdateLifetimeModal: "inventory_update_lifetime_modal",
    InventoryBulkActionModal: "inventory_bulk_action_modal",
    InventoryBulkUpdateLifetimeModal: "inventory_bulk_update_lifetime_modal",
    ProjectsPageCreateEnvironmentModal: "projects_page_create_environment_modal",
    ProjectDeleteModal: "project_delete_modal",
    ProjectInsightsDisable: "project_insights_disable",
    ProjectPrebuildsToggle: "project_prebuilds_toggle",
    EditProjectModal: "edit_project_modal",
    CreateProjectModal: "create_project_modal",
    CreateEnvironmentSCMAuthenticationModal: "create_environment_scm_authentication_modal",
    EnvironmentSCMAuthenticationModal: "environment_scm_authentication_modal",
    EnvironmentEditorPopover: "environment_editor_popover",
    EnvironmentSSHFormPopover: "environment_ssh_form_popover",
    EnvironmentOpenPortPoppover: "environment_open_port_poppover",
    EnvironmentEditPortPopover: "environment_edit_port_popover",
    EnvironmentSecretsModal: "environment_secrets_modal",
    ResourceUsageModal: "resource_usage_modal",
    EnvironmentOnboarding: "environment_onboarding",
    EnvironmentOnboardingDevcontainer: "environment_onboarding_devcontainer",
    EnvironmentOnboardingAutomations: "environment_onboarding_automations",
    EnvironmentOnboardingCommitFiles: "environment_onboarding_commit_files",
    EnvironmentOnboardingShare: "environment_onboarding_share",
    EnvironmentOnboardingDevContainerTemplateModal: "environment_onboarding_dev_container_template_modal",
    EnvironmentOnboardingAutomationsTemplateModal: "environment_onboarding_automations_template_modal",
    EnvironmentOnboardingCreateProjectFromEnvironmentModal:
        "environment_onboarding_create_project_from_environment_modal",
    DeleteSecretModal: "delete_secret_modal",
    NewSecretModal: "new_secret_modal",
    EditSecretModal: "edit_secret_modal",
    ImportEnvFileModal: "import_env_file_modal",
    ServiceAccountNewTokenModal: "service_account_new_token_modal",
    ServiceAccountTokenCreatedModal: "service_account_token_created_modal",
    ServiceAccountDeleteTokenModal: "service_account_delete_token_modal",
    NewCommandModal: "new_command_modal",
    EditCommandModal: "edit_command_modal",
    DeleteCommandModal: "delete_command_modal",
    ImportDefaultCommandsDialog: "import_default_commands_dialog",
    NewSkillModal: "new_skill_modal",
    EditSkillModal: "edit_skill_modal",
    DeleteSkillModal: "delete_skill_modal",
    ImportDefaultSkillsDialog: "import_default_skills_dialog",
    ConfigureCrowdStrikeModal: "configure_crowdstrike_modal",
    CustomSecurityAgentModal: "custom_security_agent_modal",
    DeleteRunnerModal: "delete_runner_modal",
    ForceDeleteRunnerModal: "force_delete_runner_modal",
    NewRunnerModal: "new_runner_modal",
    RenameRunnerModal: "rename_runner_modal",
    RunnerLiveLogsModal: "runner_live_logs_modal",
    RunnerWarmPoolOverviewModal: "runner_warm_pool_overview_modal",
    RenameAutomationDialog: "rename_automation_dialog",
    UpgradeRunnerModal: "upgrade_runner_modal",
    OrganizationMemberSuspendModal: "organization_member_suspend_modal",
    OrganizationMemberChangeRoleModal: "organization_member_change_role_modal",
    OrganizationMemberLeaveModal: "organization_member_leave_modal",
    OrganizationMemberDeleteModal: "organization_member_delete_modal",
    OnboardingHowGitpodWorksTab: "onboarding_how_gitpod_works_tab",
    OnboardingRunnerSelectTab: "onboarding_runner_select_tab",
    OnboardingRunnerDesktopTab: "onboarding_runner_desktop_tab",
    OnboardingRunnerSelectProviderTab: "onboarding_runner_select_provider_tab",
    OnboardingRunnerSelectTemplateStep: "onboarding_runner_select_template_step",
    OnboardingRunnerConfigureTab: "onboarding_runner_configure_tab",
    OnboardingRunnerCreateRunnerTabStep: "onboarding_runner_create_runner_tab_step",
    OnboardingRunnerConfigureCloudFormationStackTabStep: "onboarding_runner_configure_cloud_formation_stack_tab_step",
    OnboardingRunnerConfigureSCMTabStep: "onboarding_runner_configure_scm_tab_step",
    OnboardingRunnerAddSCMProviderModal: "onboarding_runner_add_scm_provider_modal",
    OnboardingRunnerEditSCMProviderModal: "onboarding_runner_edit_scm_provider_modal",
    OnboardingRunnerDeleteSCMProviderModal: "onboarding_runner_delete_scm_provider_modal",
    OnboardingRunnerConfigureEnvironmentClassesTabStep: "onboarding_runner_configure_environment_classes_tab_step",
    OnboardingRunnerAddEnvironmentClassModal: "onboarding_runner_add_environment_class_modal",
    OnboardingRunnerEditEnvironmentClassModal: "onboarding_runner_edit_environment_class_modal",
    OnboardingAutomateTab: "onboarding_automate_tab",
    OnboardingAutomateSCMAuthenticationModal: "onboarding_automate_scm_authentication_modal",
    OnboardingShareTab: "onboarding_share_tab",
    DeleteEnvironmentModal: "delete_environment_modal",
    DeleteArchivedEnvironmentModal: "delete_archived_environment_modal",
    DeleteAgentExecutionModal: "delete_agent_execution_modal",
    ResetChatModal: "reset_chat_modal",
    StartAgentExecutionModal: "start_agent_execution_modal",
    ForceDeleteEnvironmentModal: "force_delete_environment_modal",
    AddSCMProviderModal: "add_scm_provider_modal",
    EditSCMProviderModal: "edit_scm_provider_modal",
    DeleteSCMProviderModal: "delete_scm_provider_modal",
    AddLocalSCMProviderModal: "add_local_scm_provider_modal",
    CreateWorkflowDialog: "create_workflow_dialog",
    EditLocalSCMProviderModal: "edit_local_scm_provider_modal",
    DeleteLocalSCMProviderModal: "delete_local_scm_provider_modal",
    AddLLMIntegrationModal: "add_llm_integration_modal",
    EditLLMIntegrationModal: "edit_llm_integration_modal",
    DeleteLLMIntegrationModal: "delete_llm_integration_modal",
    AddEnvironmentClassModal: "add_environment_class_modal",
    EditEnvironmentClassModal: "edit_environment_class_modal",
    PrivacyPolicyNudge: "privacy_policy_nudge",

    VersionUpdateNudge: "version_update_nudge",
    AnnouncementBannerPopover: "announcement_banner_popover",
    NudgeDownloadAppPopover: "nudge_download_app_popover",
    GitAuthenticationTokenDeleteModal: "git_authentication_token_delete_modal",
    GitAuthenticationTokenCreateModal: "git_authentication_token_create_modal",
    SetupSSOModal: "setup_sso_modal",
    DeactivateSSOModal: "deactivate_sso_modal",
    TestingSSOModal: "testing_sso_modal",
    SCIMConfiguration: "scim_configuration_modal",
    SCIMTokenDisplay: "scim_token_display",
    ConfirmDeleteSCIM: "confirm_delete_scim",
    SCIMOperationsLog: "scim_operations_log",
    ContextUrlInput: "context_url_input",
    DeleteAccountModal: "delete_account_modal",
    OnaPromptInput: "ona_prompt_input",
    OnaCreateProjectModal: "ona_create_project_modal",
    TierViolationModal: "tier-violation-modal",
    CreditsExhaustionModal: "credits-exhaustion-modal",
    RecoveryCreditModal: "recovery-credit-modal",
    PaymentMethodRequiredModal: "payment-method-required-modal",
    AgentExecutionSCMAuthenticationModal: "agent_execution_scm_authentication_modal",
    AIPromptSCMAuthenticationModal: "ai_prompt_scm_authentication_modal",
    SCMAuthenticationPATRepoUrl: "scm_authentication_pat_repo_url",
    ManageEditorsModal: "manage_editors_modal",
    AgentExecutionModal: "agent_execution_modal",
    AgentConversationModal: "agent_conversation_modal",
    KeyboardShortcutsModal: "keyboard_shortcuts_modal",

    AutoDeletePolicyNudge: "auto_delete_policy_nudge",
    AutomationsLaunchNudge: "automations_launch_nudge",
    BillingModal: "billing_modal",
    ChangeSubscriptionModal: "change_subscription_modal",
    ChangeSubscriptionConfirmationModal: "change_subscription_confirmation_modal",
    AgentCommandsSettings: "agent_commands_settings",
    OverrideConversationUrlModal: "override_conversation_url_modal",
    SupportBundleUnavailableModal: "support_bundle_unavailable_modal",
    EnvironmentLifetimeExceededModal: "environment_lifetime_exceeded_modal",
    DeleteWorkflowDialog: "delete_workflow_dialog",
    CancelWorkflowExecutionDialog: "cancel_workflow_execution_dialog",
    CancelWorkflowExecutionActionDialog: "cancel_workflow_execution_action_dialog",
    SettingsGroupDetailsRemove: "settings_group_details_remove",
    SettingsGroupDelete: "settings_group_delete",
    SettingsGroupManageOrgRoles: "settings_group_manage_org_roles",
    SettingsTeamCreate: "settings_team_create",
    SettingsTeamEdit: "settings_team_edit",
    SettingsTeamDelete: "settings_team_delete",
    SettingsTeamAddMembers: "settings_team_add_members",
    ShareResourceModal: "share_resource_modal",
    SetupCustomDomainModal: "setup_custom_domain_modal",
    DeleteCustomDomainModal: "delete_custom_domain_modal",
    EnforceCustomDomainConfirmationDialog: "enforce_custom_domain_confirmation_dialog",
    ConfigureWebhookDialog: "configure_webhook_dialog",
    CreateWebhookDialog: "create_webhook_dialog",
    DeleteWebhookDialog: "delete_webhook_dialog",
    RemoveWebhookScopeDialog: "remove_webhook_scope_dialog",
    RotateWebhookSecretDialog: "rotate_webhook_secret_dialog",
    WorkflowEditPageRepoTriggerSampleDialog: "workflow-edit-page-repo-trigger-sample-dialog",
    WorkflowEditPageUnsavedChangesDialog: "workflow-edit-page-unsaved-changes-dialog",
    InviteMembersUnsentInvitesDialog: "invite-members-unsent-invites-dialog",
    PrebuildLogsModal: "prebuild_logs_modal",
    RecommendedEditorsModal: "recommended_editors_modal",
    ServiceAccountCreateDialog: "service_account_create_dialog",
    ServiceAccountEditDialog: "service_account_edit_dialog",
    ServiceAccountDeleteDialog: "service_account_delete_dialog",
    ServiceAccountAddGitAuthenticationDialog: "service_account_add_git_authentication_dialog",
    ServiceAccountGitAuthenticationDeleteDialog: "service_account_git_authentication_delete_dialog",
    RequiredIntegrationsDialog: "required_integrations_dialog",
    IntegrationConfigModal: "integration_config_modal",

    // automations related track locations
    RunAutomationModal: "run_automation_modal",
    UserSettingsModal: "user_settings_modal",
    SettingsSidebarOrgSwitcher: "settings_sidebar_org_switcher",

    // review related track locations
    ReviewSubmitDialog: "review_submit_dialog",
    ReviewDismissDialog: "review_dismiss_dialog",

    // experiments related track locations
    ExperimentsSettings: "experiments_settings",

    // integration connect page
    ConnectIntegrationOrgSwitcher: "connect_integration_org_switcher",

    // changelog
    ChangelogDialog: "changelog_dialog",

    // oidc configuration
    OidcSaveConfirmationModal: "oidc_save_confirmation_modal",

    // enterprise usage
    ExportCsvModal: "export_csv_modal",
    EditTeamBudgetDialog: "edit_team_budget_dialog",
} as const;
export type TrackLocation = (typeof TrackLocations)[keyof typeof TrackLocations];

// Helper type to add a data-track-location prop to a component
export type PropsWithTrackLocation<Props = unknown> = {
    "data-track-location"?: TrackLocation;
} & Props;

// borrowed from https://github.com/microsoft/vscode/blob/46f00e42d894e87f2b92868da12d4771c606d3c4/src/vs/platform/telemetry/common/telemetryUtils.ts#L274
export function anonymizeFilePaths(stack: string): string {
    // Fast check to see if it is a file path to avoid doing unnecessary heavy regex work
    if (!stack || (!stack.includes("/") && !stack.includes("\\"))) {
        return stack;
    }

    let updatedStack = stack;

    // eslint-disable-next-line no-useless-escape
    const nodeModulesRegex = /^[\\\/]?(node_modules|node_modules\.asar)[\\\/]/;
    // eslint-disable-next-line no-useless-escape
    const fileRegex = /(file:\/\/)?([a-zA-Z]:(\\\\|\\|\/)|(\\\\|\\|\/))?([\w-\._]+(\\\\|\\|\/))+[\w-\._]*/g;
    let lastIndex = 0;
    updatedStack = "";

    for (let result = fileRegex.exec(stack); result !== null; result = fileRegex.exec(stack)) {
        // anoynimize user file paths that do not need to be retained or cleaned up.
        if (!nodeModulesRegex.test(result[0])) {
            updatedStack += stack.substring(lastIndex, result.index) + "<REDACTED: user-file-path>";
            lastIndex = fileRegex.lastIndex;
        }
    }
    if (lastIndex < stack.length) {
        updatedStack += stack.substring(lastIndex);
    }

    return updatedStack;
}

export function anonymizeUrls(label: string | undefined): string | undefined {
    if (!label?.includes("http://") && !label?.includes("https://")) {
        // quick check for early return
        return label;
    }

    let anonymizedLabel = "";
    let lastIndex = 0;

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    for (let result = urlRegex.exec(label); result !== null; result = urlRegex.exec(label)) {
        anonymizedLabel += label.substring(lastIndex, result.index) + "<REDACTED: user-url>";
        lastIndex = urlRegex.lastIndex;
    }

    if (lastIndex < label.length) {
        anonymizedLabel += label.substring(lastIndex);
    }

    return anonymizedLabel;
}
